import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import SaleBanner from './components/SaleBanner';
import DesktopToolSidebar from './components/DesktopToolSidebar';
import Login from './pages/Login';
import Signup from './pages/Signup';
import GoogleSignup from './pages/GoogleSignup';
import Terms from './pages/Terms';
import HomeWithStoryCTA from './pages/HomeWithStoryCTA';
import RecruitmentList from './pages/RecruitmentList';
import RecruitmentDetail from './pages/RecruitmentDetail';
import CreateRecruitment from './pages/CreateRecruitment';
import CreateCircle from './pages/CreateCircle';
import CircleManage from './pages/CircleManage';
import CirclePostCreate from './pages/circle/CirclePostCreate';
import CirclePostDetail from './pages/circle/CirclePostDetail';
import CircleEventCreate from './pages/circle/CircleEventCreate';
import CircleEventDetail from './pages/circle/CircleEventDetail';
import EditRecruitment from './pages/EditRecruitment';
import ApplicationHistory from './pages/ApplicationHistory';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import ChangeEmail from './pages/ChangeEmail';
import ChangePassword from './pages/ChangePassword';
import MountainLog from './pages/MountainLog';
import FareSplitPage from './pages/FareSplitPage';
import MountainTypePage from './pages/MountainTypePage';
import Achievements from './pages/Achievements';
import Contact from './pages/Contact';
import CommunityGuidelines from './pages/CommunityGuidelines';
import FirstTimeGuide from './pages/FirstTimeGuide';
import Faq from './pages/Faq';
import StorySubmission from './pages/StorySubmission';
import AdminKpiReport from './pages/AdminKpiReport';
import EmailDeliveryTest from './pages/EmailDeliveryTest';
import Chat from './pages/Chat';
import ChatList from './pages/ChatList';
import Notifications from './pages/Notifications';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import UserProfile from './pages/UserProfile';
import MyRecruitments from './pages/MyRecruitments';
import GroupChat from './pages/GroupChat';
import BoardHome from './pages/board/BoardHome';
import BoardCategory from './pages/board/BoardCategory';
import ThreadList from './pages/board/ThreadList';
import ThreadDetail from './pages/board/ThreadDetail';
import NewThread from './pages/board/NewThread';
import BoardSearch from './pages/board/BoardSearch';
import ProtectedRoute from './components/ProtectedRoute';
import BottomNav from './components/BottomNav';
import Footer from './components/Footer';
import RouteAnalytics from './components/RouteAnalytics';
import ToastHost from './components/ToastHost';
import NotificationDeliveryRegistration from './components/NotificationDeliveryRegistration';
import { MOUNTAIN_AREAS_LIST } from './utils/constants';
import {
  getAgeGroupLabelBySlug,
  getAgeGroupPath,
  getPrefectureAgePath,
  getPrefectureBySlug,
  getPrefecturePath
} from './utils/seoRoutes';
import './App.css';

const APP_NAME = 'ヤマッチ';
const HOME_SEO_TITLE = 'ヤマッチ「登山仲間募集サイト」';

const INDEXABLE_PATHS = new Set([
  '/',
  '/blog/create-circle',
]);

const SITE_ORIGIN = 'https://yamano-media.com';
const SITE_BASE_PATH = '/blog/yamanoboro/';

function getPrefectureSeoName(prefecture) {
  if (!prefecture) return '';
  if (prefecture === '北海道') return prefecture;
  return prefecture.replace(/[都府県]$/, '');
}

function upsertMeta(name, content) {
  let meta = document.head.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function upsertPropertyMeta(property, content) {
  let meta = document.head.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function upsertCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

function getMountainAreaName(areaId) {
  return MOUNTAIN_AREAS_LIST.find((area) => area.id === areaId)?.name || '';
}

function isIndexablePath(pathname) {
  if (INDEXABLE_PATHS.has(pathname)) return true;
  return false;
}

function getCanonicalUrl(location) {
  if (location.pathname === '/blog/create-circle' || location.pathname === '/blog/create-circle/') {
    return `${SITE_ORIGIN}/blog/create-circle/`;
  }

  const params = new URLSearchParams(location.search);
  const type = params.get('type') || '';
  const canonicalParams = new URLSearchParams();
  if (type) canonicalParams.set('type', type);

  let canonicalPath = location.pathname === '/' ? '/' : location.pathname;
  if (location.pathname === '/recruitments') {
    const prefecture = params.get('prefecture') || '';
    const area = params.get('area') || '';
    const age = params.get('age') || '';
    if (prefecture && age) canonicalPath = getPrefectureAgePath(prefecture, age);
    else if (prefecture) canonicalPath = getPrefecturePath(prefecture);
    else if (area) canonicalPath = `/a/${area}`;
    else if (age) canonicalPath = getAgeGroupPath(age);
  }

  const query = canonicalParams.toString();
  const path = canonicalPath === '/' ? '' : canonicalPath.replace(/^\//, '');
  return `${SITE_ORIGIN}${SITE_BASE_PATH}${path}${query ? `?${query}` : ''}`;
}

function buildRecruitmentListSeo(location) {
  const params = new URLSearchParams(location.search);
  const pathParts = location.pathname.split('/').filter(Boolean);
  const prefecture = pathParts[0] === 'p'
    ? getPrefectureBySlug(pathParts[1])
    : params.get('prefecture') || '';
  const areaId = pathParts[0] === 'a'
    ? pathParts[1]
    : params.get('area') || '';
  const ageSlug = pathParts[0] === 'age'
    ? pathParts[1]
    : pathParts[0] === 'p' && pathParts[2]
      ? pathParts[2]
      : params.get('age') || '';
  const type = params.get('type') || '';
  const normalizedPrefecture = getPrefectureSeoName(prefecture);
  const areaName = getMountainAreaName(areaId);
  const ageLabel = getAgeGroupLabelBySlug(ageSlug);
  const isGroup = type === 'group';

  if (normalizedPrefecture && ageLabel) {
    return {
      title: `登山サークル ${normalizedPrefecture} ${ageLabel}｜メンバー募集一覧 | ${APP_NAME}`,
      description: `${normalizedPrefecture}で${ageLabel}が参加しやすい登山サークル・メンバー募集一覧です。所属メンバー募集を年代と地域から探せます。`,
    };
  }

  if (normalizedPrefecture) {
    const title = isGroup
      ? `登山サークル ${normalizedPrefecture}｜メンバー募集一覧 | ${APP_NAME}`
      : `登山サークル ${normalizedPrefecture}・登山仲間募集 | ${APP_NAME}`;
    const description = `${normalizedPrefecture}の登山サークル・登山仲間募集一覧です。所属メンバー募集、同行者募集、日帰り登山の募集を都道府県別に探せます。`;
    return { title, description };
  }

  if (areaName) {
    return {
      title: `${areaName}の登山仲間募集・登山サークル | ${APP_NAME}`,
      description: `${areaName}周辺の登山仲間募集一覧です。山域から同行者募集や登山サークルのメンバー募集を探せます。`,
    };
  }

  if (ageLabel) {
    return {
      title: `登山サークル ${ageLabel}｜メンバー募集一覧 | ${APP_NAME}`,
      description: `${ageLabel}が参加しやすい登山サークル・メンバー募集一覧です。所属メンバー募集を年代別に探せます。`,
    };
  }

  return {
    title: `登山サークル・登山仲間募集一覧 | ${APP_NAME}`,
    description: '全国の登山サークル・登山仲間募集一覧です。都道府県や登山エリアから、同行者募集や所属メンバー募集を探せます。',
  };
}

function AppSeo() {
  const location = useLocation();
  const { pathname, search } = location;

  useEffect(() => {
    const currentLocation = { pathname, search };
    const isRecruitmentList = pathname === '/recruitments' || /^\/p\/[^/]+(\/[^/]+)?$/.test(pathname) || /^\/a\/[^/]+$/.test(pathname) || /^\/age\/[^/]+$/.test(pathname);
    let title = `${APP_NAME}`;
    let description = 'ヤマッチは全国の登山仲間を探せる登山仲間募集サイトです。';

    if (pathname === '/') {
      title = HOME_SEO_TITLE;
      description = 'ヤマッチは全国の登山仲間を探せる登山仲間募集サイトです。都道府県別に登山サークル、登山メンバー募集、同行者募集を確認できます。';
    } else if (pathname === '/create-circle' || pathname === '/blog/create-circle' || pathname === '/blog/create-circle/') {
      title = `登山サークルを作成する | ${APP_NAME}`;
      description = 'ヤマッチで登山サークルを作成し、メンバー募集を公開できます。活動エリア、サークル文化、安全方針を登録して登山仲間を募集しましょう。';
    } else if (isRecruitmentList) {
      const seo = buildRecruitmentListSeo(currentLocation);
      title = seo.title;
      description = seo.description;
    }

    document.title = title;
    upsertMeta('description', description);
    upsertMeta('keywords', '登山サークル, 登山仲間募集, 登山メンバー募集, 登山同行者募集, 山友募集');
    upsertPropertyMeta('og:title', title);
    upsertPropertyMeta('og:description', description);
    upsertPropertyMeta('og:url', getCanonicalUrl(currentLocation));
    upsertMeta('twitter:title', title);
    upsertMeta('twitter:description', description);
    upsertCanonical(getCanonicalUrl(currentLocation));

    const robots = isIndexablePath(pathname)
      ? 'index, follow'
      : 'noindex, nofollow';
    upsertMeta('robots', robots);
  }, [pathname, search]);

  return null;
}

function HashRouteRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!window.location.hash.startsWith('#/')) return;

    const hashPath = window.location.hash.slice(1);
    navigate(hashPath, { replace: true });
  }, [navigate]);

  return null;
}

function AppLayout() {
  const location = useLocation();
  const hideSidebar = [
    '/login',
    '/signup',
    '/reset-password',
    '/google-signup',
    '/verify-email',
    '/change-email',
    '/change-password',
    '/create-recruitment',
    '/create-circle',
    '/profile/edit',
  ].some((prefix) => location.pathname.startsWith(prefix)) ||
    location.pathname.startsWith('/blog/create-circle') ||
    location.pathname.startsWith('/edit-recruitment/') ||
    /^\/circles\/[^/]+\/posts\/[^/]+\/edit/.test(location.pathname) ||
    /^\/circles\/[^/]+\/events\/[^/]+\/edit/.test(location.pathname) ||
    /^\/circles\/[^/]+\/(manage|posts\/new|events\/new)/.test(location.pathname) ||
    location.pathname.startsWith('/chat/') ||
    location.pathname.startsWith('/group-chat/') ||
    location.pathname.startsWith('/email-delivery-test') ||
    location.pathname.startsWith('/ops-report');

  return (
    <div className="app">
      <AppSeo />
      <HashRouteRedirect />
      <RouteAnalytics />
      <ToastHost />
      <NotificationDeliveryRegistration />
      <SaleBanner />
      <Navbar />
      <div className={`app-layout ${hideSidebar ? 'app-layout--full' : ''}`}>
        <main className="main-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/google-signup" element={<GoogleSignup />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/" element={<HomeWithStoryCTA />} />
            <Route path="/mountain-log" element={<MountainLog />} />
            <Route path="/fare-split" element={<FareSplitPage />} />
            <Route path="/mountain-type" element={<MountainTypePage />} />
            <Route path="/achievements" element={<Achievements />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/first-time-guide" element={<FirstTimeGuide />} />
            <Route path="/faq" element={<Faq />} />
            <Route
              path="/ops-report"
              element={
                <ProtectedRoute>
                  <AdminKpiReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-delivery-test"
              element={
                <ProtectedRoute>
                  <EmailDeliveryTest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stories/submit"
              element={
                <ProtectedRoute>
                  <StorySubmission />
                </ProtectedRoute>
              }
            />
            <Route path="/recruitments" element={<RecruitmentList />} />
            <Route path="/p/:prefectureSlug" element={<RecruitmentList />} />
            <Route path="/p/:prefectureSlug/:ageSlug" element={<RecruitmentList />} />
            <Route path="/a/:areaId" element={<RecruitmentList />} />
            <Route path="/age/:ageSlug" element={<RecruitmentList />} />
            <Route path="/recruitments/:id" element={<RecruitmentDetail />} />
            <Route path="/create-circle" element={<CreateCircle />} />
            <Route path="/blog/create-circle" element={<CreateCircle />} />
            <Route
              path="/circles/:id/manage"
              element={
                <ProtectedRoute>
                  <CircleManage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/circles/:id/posts/new"
              element={
                <ProtectedRoute>
                  <CirclePostCreate />
                </ProtectedRoute>
              }
            />
            <Route path="/circles/:id/posts/:postId" element={<CirclePostDetail />} />
            <Route
              path="/circles/:id/posts/:postId/edit"
              element={
                <ProtectedRoute>
                  <CirclePostCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/circles/:id/events/new"
              element={
                <ProtectedRoute>
                  <CircleEventCreate />
                </ProtectedRoute>
              }
            />
            <Route path="/circles/:id/events/:eventId" element={<CircleEventDetail />} />
            <Route
              path="/circles/:id/events/:eventId/edit"
              element={
                <ProtectedRoute>
                  <CircleEventCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create-recruitment"
              element={
                <ProtectedRoute>
                  <CreateRecruitment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-recruitment/:id"
              element={
                <ProtectedRoute>
                  <EditRecruitment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <ApplicationHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/edit"
              element={
                <ProtectedRoute>
                  <EditProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-email"
              element={
                <ProtectedRoute>
                  <ChangeEmail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat/:chatId"
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/group-chat/:recruitmentId"
              element={
                <ProtectedRoute>
                  <GroupChat />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chats"
              element={
                <ProtectedRoute>
                  <ChatList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-recruitments"
              element={
                <ProtectedRoute>
                  <MyRecruitments />
                </ProtectedRoute>
              }
            />
            <Route path="/users/:uid" element={<UserProfile />} />
            {/* 掲示板（Phase 2） */}
            <Route path="/board" element={<BoardHome />} />
            <Route path="/board/search" element={<BoardSearch />} />
            <Route path="/board/new-thread" element={<ProtectedRoute><NewThread /></ProtectedRoute>} />
            <Route path="/board/:categoryId" element={<BoardCategory />} />
            <Route path="/board/:categoryId/:subCategoryId" element={<ThreadList />} />
            <Route path="/board/:categoryId/:subCategoryId/:threadId" element={<ThreadDetail />} />
          </Routes>
        </main>
        {!hideSidebar && <DesktopToolSidebar />}
      </div>
      <Footer />
      <BottomNav />
    </div>
  );
}

function App() {
  const basename = window.location.pathname.startsWith(SITE_BASE_PATH)
    ? SITE_BASE_PATH.replace(/\/$/, '')
    : '';

  return (
    <AuthProvider>
      <Router basename={basename}>
        <AppLayout />
      </Router>
    </AuthProvider>
  );
}

export default App;
